// @ts-ignore
import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState
} from "react";

import {
    StatsigExperimentCohort,
    StatsigExperimentKey
} from "../../../../feature-gates/types";
import { TemplateOptionMetadata } from "../components/atlas-61649-template-choice/team-type-template-map";

import { useConfirmationPage } from "./use-confirmation-page";
import { ExperimentEnrollment } from "./use-experiment";

import {
    isUserInBusinessCategory,
    useFetchUserSegmentData,
    useSearchParameters
} from "./";

export type JiraTemplateChoice = "kanban" | "scrum" | "business" | undefined;

const jiraTemplateChoiceTypeCheck = (
    s: string | undefined
): JiraTemplateChoice => {
    switch (s) {
        case "kanban":
            return "kanban";
            break;
        case "scrum":
            return "scrum";
            break;
        case "business":
            return "business";
            break;
        default:
            return undefined;
    }
};

type JiraTemplateChoiceProps = {
    shouldShowJiraTemplateChoiceScreen: boolean;
    loadingUserSegmentData: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceId?: string;
    setTemplateChoiceId: (templateChoiceId?: string) => void;
    templateChoiceName?: string;
    setTemplateChoiceName: (templateChoiceName?: string) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
    goBackToUserSegmentation: () => void;
    templateMetadata?: TemplateOptionMetadata;
    setTemplateMetadata: React.Dispatch<
        React.SetStateAction<TemplateOptionMetadata | undefined>
    >;
};

/**
 * TODO: refector this provider to only store template choice information and loadingUserSegmentData.
 * The viewing of this screen will be controlled by the jira screen config and usage of the useScreenManager hook
 * to navigate to the previous of next screen.
 * */
export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        shouldShowJiraTemplateChoiceScreen: false,
        loadingUserSegmentData: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        setTemplateChoiceId: () => undefined,
        setTemplateChoiceName: () => undefined,
        templateChoiceId: undefined,
        templateChoiceName: undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined,
        goBackToUserSegmentation: () => undefined,
        templateMetadata: undefined,
        setTemplateMetadata: () => undefined
    }
);

export const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const { products } = useSearchParameters();

    /**
     * TODO: Once the confirmation rearchitecture is complete, the hook useCanShowJiraTemplateChoiceScreen can be removed.
     *  This is because the jira screen config will be responsible for determining if the template choice screen should show.
     **/
    const {
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData,
        setUserSegmentationPageIsFinished
    } = useCanShowJiraTemplateChoiceScreen(products);

    /**
     * TODO: Once the confirmation rearchitecture is complete, this function can be removed.
     * This is because the hook useScreenManager will provide the function to navigate back to the user segmentation view.
     **/
    const goBackToUserSegmentation = useCallback(
        () => setUserSegmentationPageIsFinished(false),
        []
    );

    const { templateChoice: templateChoiceQueryParam } = useSearchParameters();

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >(jiraTemplateChoiceTypeCheck(templateChoiceQueryParam));

    const [templateChoiceId, setTemplateChoiceId] = useState<
        string | undefined
    >();

    const [templateChoiceName, setTemplateChoiceName] = useState<
        string | undefined
    >();

    const [templateMetadata, setTemplateMetadata] =
        useState<TemplateOptionMetadata>();

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    // TODO: If userHasPickedTemplate, call useScreenManager.nextScreen
    const userHasPickedTemplate =
        templateChoice !== undefined || templateChoiceId !== undefined;

    // TODO: remove shouldShowJiraTemplateChoiceScreen. Screen visibility will be controlled by jira screen config instead.
    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined &&
        canShowJiraTemplateChoiceScreen &&
        !userHasPickedTemplate;

    return (
        <JiraTemplateChoiceContext.Provider
            value={{
                templateChoice,
                loadingUserSegmentData,
                setTemplateChoice,
                setTemplateChoiceId,
                setTemplateChoiceName,
                templateChoiceId,
                templateChoiceName,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError,
                goBackToUserSegmentation,
                templateMetadata,
                setTemplateMetadata
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};
/* The user must pass the following conditions to see the template choice screen
   - !hasTemplateRecommendation: Supplied via a query param, there must be no `templateId`
   - isProvisioningOnlyJSW: Provisioning only JSW and no other products
   - isN2NUser: if n2n=true query param (new user signing up for first instance)
   - !isBusinessSegmentUser: The user must not be counted as a business user by user segmentation
   - isUserSegDon: if the user seg screen was marked as finished or hidden by a query param
   - !fetchError: This hook must access /gateway/api/me to check user segmentation and this call must succeed
     in order for the check above to be valid
 */
export const useCanShowJiraTemplateChoiceScreen = (
    products: readonly string[]
) => {
    const {
        userSegmentationContext: {
            shouldShowUserSegmentationPage,
            userSegmentationPageIsFinished,
            setUserSegmentationPageIsFinished,
            selectedTeamType = ""
        },
        experiments
    } = useConfirmationPage();

    const transparentTemplateForAllTeamsExperiment = useMemo(
        () =>
            experiments.get(
                StatsigExperimentKey.JFIND_TRANSPARENT_TEMPLATE_FOR_ALL_TEAMS
            ) ?? ({} as ExperimentEnrollment),
        [experiments]
    );

    const { templateId } = useSearchParameters();
    const hasTemplateRecommendation = Boolean(templateId);
    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";
    const isBusinessSegmentUserFromUserSegState = useMemo(
        () =>
            isUserInBusinessCategory({
                teamType: selectedTeamType,
                role: ""
            }),
        [selectedTeamType]
    );

    const { fetched, isBusinessSegmentUser: isBusinessSegmentFromRequest } =
        useFetchUserSegmentData(userSegmentationPageIsFinished);

    let canShowJiraTemplateChoiceScreen =
        isProvisioningOnlyJSW &&
        !hasTemplateRecommendation &&
        !shouldShowUserSegmentationPage;

    if (selectedTeamType) {
        // Only check for business segment if user is not enrolled or is not in treatment
        if (
            !transparentTemplateForAllTeamsExperiment.enrollmentPreconditionsMet ||
            transparentTemplateForAllTeamsExperiment.cohort !==
                StatsigExperimentCohort.JFIND_TRANSPARENT_TEMPLATE_FOR_ALL_TEAMS_VARIANT
        ) {
            canShowJiraTemplateChoiceScreen =
                canShowJiraTemplateChoiceScreen &&
                !isBusinessSegmentUserFromUserSegState;
        }
    } else {
        canShowJiraTemplateChoiceScreen =
            canShowJiraTemplateChoiceScreen && !isBusinessSegmentFromRequest;
    }

    return {
        setUserSegmentationPageIsFinished,
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData: !fetched
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};
